import { useEffect, useRef, useState, type ClipboardEvent, type FormEvent, type KeyboardEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Bracket, RingaLogo } from '../brand/RingaLogo';
import { CodeFx } from '../brand/CodeFx';
import { Rings } from '../brand/Rings';
import { Scramble } from '../brand/Scramble';
import { api, ApiError, errorMessage } from '../lib/api';
import { armDoor } from '../lib/auth';
import type { Me } from '../lib/types';

const RESEND_S = 60;

// only same-app paths, so ?next= can never send anyone elsewhere
const safeNext = (next: string | null, role: Me['role']): string | null => {
  if (!next || !next.startsWith('/') || next.startsWith('//')) return null;
  if (next.startsWith('/admin')) return role === 'admin' ? next : null;
  return next.startsWith('/p/') || next === '/propostas' ? next : null;
};

const CodeInput = ({ disabled, onComplete }: { disabled: boolean; onComplete: (code: string) => void }) => {
  const [digits, setDigits] = useState<string[]>(Array(6).fill(''));
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => refs.current[0]?.focus(), []);

  const set = (next: string[]) => {
    setDigits(next);
    if (next.every((d) => d)) onComplete(next.join(''));
  };
  const fill = (from: number, text: string) => {
    const clean = text.replace(/\D/g, '').slice(0, 6 - from).split('');
    if (!clean.length) return;
    const next = [...digits];
    clean.forEach((d, i) => (next[from + i] = d));
    refs.current[Math.min(from + clean.length, 5)]?.focus();
    set(next);
  };
  const onKey = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      const next = [...digits];
      next[i - 1] = '';
      setDigits(next);
      refs.current[i - 1]?.focus();
      e.preventDefault();
    } else if (e.key === 'ArrowLeft' && i > 0) refs.current[i - 1]?.focus();
    else if (e.key === 'ArrowRight' && i < 5) refs.current[i + 1]?.focus();
  };

  return (
    <div className="code-in" role="group" aria-label="Código de 6 dígitos">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          className="code-box"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          aria-label={`Dígito ${i + 1}`}
          maxLength={6}
          value={d}
          disabled={disabled}
          onKeyDown={(e) => onKey(i, e)}
          onPaste={(e: ClipboardEvent<HTMLInputElement>) => {
            e.preventDefault();
            fill(i, e.clipboardData.getData('text'));
          }}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, '');
            if (v.length > 1) return fill(i, v);
            const next = [...digits];
            next[i] = v;
            if (v && i < 5) refs.current[i + 1]?.focus();
            set(next);
          }}
        />
      ))}
    </div>
  );
};

export const Login = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [wait, setWait] = useState(0);
  const [attempt, setAttempt] = useState(0);

  // already signed in: skip the door
  useEffect(() => {
    api<Me>('/api/auth/me')
      .then(async (me) => {
        const next = safeNext(params.get('next'), me.role);
        if (next) return navigate(next, { replace: true });
        if (me.role === 'admin') return navigate('/admin', { replace: true });
        navigate('/propostas', { replace: true });
      })
      .catch(() => undefined);
  }, [navigate, params]);

  useEffect(() => {
    if (wait <= 0) return;
    const t = window.setTimeout(() => setWait((w) => w - 1), 1000);
    return () => window.clearTimeout(t);
  }, [wait]);

  const requestCode = async (e?: FormEvent) => {
    e?.preventDefault();
    setError('');
    setBusy(true);
    try {
      const res = await api<{ redirect?: string; role?: Me['role'] }>('/api/auth/request-code', { body: { email } });
      // dev bypass: signed in straight away, no code
      if (res.redirect && res.role) {
        armDoor();
        navigate(safeNext(params.get('next'), res.role) ?? res.redirect, { replace: true });
        return;
      }
      setStep('code');
      setWait(RESEND_S);
    } catch (err) {
      // a code went out less than a minute ago: it is still valid, so go type it
      if (err instanceof ApiError && err.code === 'too_soon') {
        setStep('code');
        setError('Já enviamos um código há menos de um minuto. Use o mesmo.');
      } else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (code: string) => {
    setError('');
    setBusy(true);
    try {
      const res = await api<{ role: Me['role']; redirect: string }>('/api/auth/verify', { body: { email, code } });
      armDoor();
      navigate(safeNext(params.get('next'), res.role) ?? res.redirect, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
      setAttempt((a) => a + 1);
      setBusy(false);
    }
  };

  return (
    <main className="gate">
      <div className="gate-glow" aria-hidden="true" />
      <Rings />
      <CodeFx />

      <header className="gate-top enter" style={{ ['--d' as string]: '.05s' }}>
        <RingaLogo className="gate-logo" />
      </header>

      <section className="gate-in" aria-live="polite">
        <p className="tag enter" style={{ ['--d' as string]: '.1s' }}>
          <Bracket side="l" className="tag-b" />
          <span>AI First Software Company.</span>
          <Bracket side="r" className="tag-b" />
        </p>

        {step === 'email' ? (
          <>
            <h1 className="gate-h enter" style={{ ['--d' as string]: '.18s' }}>
              <span className="l1">Sua proposta</span> <span className="l2">está aqui.</span>
            </h1>
            <p className="gate-lead enter" style={{ ['--d' as string]: '.26s' }}>
              Entre com o e-mail que recebeu a proposta. O código de acesso chega na hora.
            </p>
            <form className="gate-card panel enter" style={{ ['--d' as string]: '.34s' }} onSubmit={requestCode}>
              <label className="field">
                <span>Seu e-mail</span>
                <input
                  className="input"
                  type="email"
                  name="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder="voce@empresa.com.br"
                  required
                  autoFocus
                  value={email}
                  aria-invalid={!!error}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
              {error && <p className="error">{error}</p>}
              <button className="btn btn-lg" type="submit" disabled={busy || !email}>
                {busy ? <span className="spinner" style={{ width: 18, height: 18 }} /> : <Scramble>Receber código</Scramble>}
              </button>
            </form>
          </>
        ) : (
          <>
            <h1 className="gate-h enter">
              <span className="l1">Confira o seu e-mail.</span> <span className="l2">O código chegou.</span>
            </h1>
            <p className="gate-lead enter" style={{ ['--d' as string]: '.08s' }}>
              Se <strong>{email}</strong> tiver uma proposta da Ringa, o código de 6 dígitos já está na sua caixa de entrada. Ele vale por 10 minutos.
            </p>
            <div className="gate-card panel enter" style={{ ['--d' as string]: '.16s' }}>
              <CodeInput key={attempt} disabled={busy} onComplete={verify} />
              {error && <p className="error">{error}</p>}
              <div className="gate-row">
                <button type="button" className="btn-link" onClick={() => (setStep('email'), setError(''))}>
                  Trocar e-mail
                </button>
                {wait > 0 ? (
                  <span className="dim num">Reenviar em {wait} s</span>
                ) : (
                  <button type="button" className="btn-link" onClick={() => requestCode()} disabled={busy}>
                    Reenviar código
                  </button>
                )}
              </div>
              {busy && (
                <div className="gate-busy">
                  <span className="spinner" />
                </div>
              )}
            </div>
            <p className="gate-help enter" style={{ ['--d' as string]: '.24s' }}>
              Não chegou? Confira o spam ou fale com quem enviou a proposta.
            </p>
          </>
        )}
      </section>

      <footer className="gate-foot">
        <span>Informação confidencial. O conteúdo desta plataforma é destinado exclusivamente ao destinatário e não deve ser compartilhado sem autorização da Ringa.</span>
        <span>© 2026 Ringa</span>
      </footer>
    </main>
  );
};
